/**
 * The 3D camera of the graph: turns a point of the 3D world into a point of the flat picture,
 * and back. Pure functions on plain data (tested in `camera.test.ts`); the {@link Scene} uses
 * them when the graph is shown in 3D, and the usual pan and zoom are applied afterwards.
 *
 * The camera turns around a `pivot` (the middle of the graph): `yaw` turns left/right (around
 * the vertical axis), `pitch` tilts up/down (around the horizontal axis). Perspective makes
 * what is far smaller: a point at `depth` behind the pivot is drawn at
 * `scale = distance / (distance + depth)` of its size. At the pivot's depth the scale is 1, so
 * the picture matches the 2D one there.
 *
 * Coordinates: `x` to the right, `y` down (as on screen), `z` away from the viewer.
 */

/** A point in the 3D world. */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Where the camera looks from. */
export interface Camera {
  /** Turn around the vertical axis, in radians. */
  yaw: number;
  /** Tilt around the horizontal axis, in radians (kept between {@link MAX_PITCH} and its negative). */
  pitch: number;
  /** How far the eye is from the pivot (world units): smaller means stronger perspective. */
  distance: number;
  /** The point the camera turns around. */
  pivot: Vec3;
}

/** A world point as drawn: its place in the flat picture, how much it shrinks, and how far it is. */
export interface Projected {
  x: number;
  y: number;
  /** Size factor from the perspective (1 at the pivot's depth, smaller when farther). */
  scale: number;
  /** Distance behind the pivot's depth, along the view (negative: in front of it). */
  depth: number;
}

/** The most the camera tilts, just short of looking straight down, so the view never flips. */
export const MAX_PITCH = Math.PI / 2 - 0.05;

/** The closest a point can come to the eye, as a share of `distance` (avoids dividing by zero). */
const NEAREST = 0.85;

/** A camera looking straight at the pivot, slightly from above so depth reads at once. */
export function defaultCamera(): Camera {
  return { yaw: 0.5, pitch: 0.35, distance: 900, pivot: { x: 0, y: 0, z: 0 } };
}

/** Projects a world point into the flat picture. */
export function project(cam: Camera, p: Vec3): Projected {
  const dx = p.x - cam.pivot.x;
  const dy = p.y - cam.pivot.y;
  const dz = p.z - cam.pivot.z;
  const cy = Math.cos(cam.yaw);
  const sy = Math.sin(cam.yaw);
  const cp = Math.cos(cam.pitch);
  const sp = Math.sin(cam.pitch);
  // Turn around the vertical axis, then tilt.
  const x1 = cy * dx - sy * dz;
  const z1 = sy * dx + cy * dz;
  const y2 = cp * dy - sp * z1;
  const z2 = sp * dy + cp * z1;
  const scale = cam.distance / (cam.distance + Math.max(z2, -cam.distance * NEAREST));
  return { x: cam.pivot.x + x1 * scale, y: cam.pivot.y + y2 * scale, scale, depth: z2 };
}

/**
 * The world point drawn at `(x, y)` of the flat picture, at `depth` behind the pivot: the
 * inverse of {@link project}. Used to move a node under the pointer without changing its depth.
 */
export function unproject(cam: Camera, x: number, y: number, depth: number): Vec3 {
  const d = Math.max(depth, -cam.distance * NEAREST);
  const scale = cam.distance / (cam.distance + d);
  const x1 = (x - cam.pivot.x) / scale;
  const y2 = (y - cam.pivot.y) / scale;
  const cy = Math.cos(cam.yaw);
  const sy = Math.sin(cam.yaw);
  const cp = Math.cos(cam.pitch);
  const sp = Math.sin(cam.pitch);
  // Undo the tilt, then the turn.
  const dy = cp * y2 + sp * d;
  const z1 = -sp * y2 + cp * d;
  const dx = cy * x1 + sy * z1;
  const dz = -sy * x1 + cy * z1;
  return { x: cam.pivot.x + dx, y: cam.pivot.y + dy, z: cam.pivot.z + dz };
}

/**
 * The direction (length 1, away from the eye) of the line of sight through `(x, y)` of the
 * flat picture: every world point drawn there lies on it. Two points of it at different depths
 * give its direction (the line is straight: {@link unproject} is linear in the depth).
 */
export function rayThrough(cam: Camera, x: number, y: number): Vec3 {
  const near = unproject(cam, x, y, 0);
  const far = unproject(cam, x, y, cam.distance);
  const dx = far.x - near.x;
  const dy = far.y - near.y;
  const dz = far.z - near.z;
  const length = Math.hypot(dx, dy, dz) || 1;
  return { x: dx / length, y: dy / length, z: dz / length };
}

/** Turns the camera by `dYaw` and tilts it by `dPitch` (radians); the tilt stays within {@link MAX_PITCH}. */
export function orbit(cam: Camera, dYaw: number, dPitch: number): void {
  cam.yaw = wrap(cam.yaw + dYaw);
  cam.pitch = Math.min(Math.max(cam.pitch + dPitch, -MAX_PITCH), MAX_PITCH);
}

/** An angle brought back between -π and π. */
function wrap(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

/**
 * Centres the camera on some points: the pivot goes to their middle and the distance grows
 * with their spread, so the perspective is about as strong for a small graph as for a big one.
 */
export function frame(cam: Camera, points: Vec3[]): void {
  if (!points.length) return;
  const c = { x: 0, y: 0, z: 0 };
  for (const p of points) {
    c.x += p.x / points.length;
    c.y += p.y / points.length;
    c.z += p.z / points.length;
  }
  let spread = 0;
  for (const p of points) spread = Math.max(spread, Math.hypot(p.x - c.x, p.y - c.y, p.z - c.z));
  cam.pivot = c;
  cam.distance = Math.max(600, spread * 2.4);
}
